/**
 * PartTwoSlides.tsx
 * -----------------
 * Learning slides for crash course part two.
 * - Multilingual content and video.
 * - Updates progress and points on completion.
 */


import React from 'react';
import { useProgress } from '../../../context/ProgressContext';
import Button from '../../common/Button';
import { useHistory } from 'react-router-dom';
import Box from '../../common/Box';

const t = {
  slide1_title: {
    fi: 'Matkailijan varoitukset Suomen kylmässä ilmastossa',
    en: "Tourist Cautions in Finland's Cold Climate",
    sv: 'Turistvarningar i Finlands kalla klimat',
  },
  slide1_content: {
    fi: 'Tässä osiossa opit yleisimmät virheet, joita matkailijat tekevät kylmissä olosuhteissa, ja kuinka välttää ne.',
    en: "In this section, you'll learn common mistakes tourists make in cold conditions and how to avoid them.",
    sv: 'I det här avsnittet lär du dig vanliga misstag turister gör i kalla förhållanden och hur du undviker dem.',
  },
  slide2_title: {
    fi: 'Sään aliarvioiminen',
    en: 'Underestimating the Weather',
    sv: 'Att underskatta vädret',
  },
  slide2_content: {
    fi: 'Älä aliarvioi Suomen kylmää säätä – pukeudu kerroksittain ja valmistaudu äkillisiin säämuutoksiin.',
    en: "Don't underestimate Finland's cold weather – dress in layers and be prepared for sudden weather changes.",
    sv: 'Underskatta inte Finlands kalla väder – klä dig i lager och var beredd på snabba väderförändringar.',
  },
  slide3_title: {
    fi: 'Väärä varustautuminen ulkoiluun',
    en: 'Improper Outdoor Gear',
    sv: 'Felaktig utrustning för utomhusbruk',
  },
  slide3_content: {
    fi: 'Vältä tavallisia varustevirheitä, kuten liian ohuita kenkiä tai liian kevyttä vaatetusta.',
    en: 'Avoid common gear mistakes like wearing too thin shoes or insufficient clothing.',
    sv: 'Undvik vanliga utrustningsmisstag som att ha för tunna skor eller otillräckliga kläder.',
  },
  video_title: {
    fi: 'Katso: Turvallinen matkailu kylmässä ilmastossa',
    en: 'Watch: Safe Tourism in Cold Climate',
    sv: 'Titta: Säker turism i kallt klimat',
  },
  back: {
    fi: 'Takaisin kurssi yleisnäkymään',
    en: 'Back to course overview',
    sv: 'Tillbaka till kursöversikten',
  },
};

const YOUTUBE_ID = 'vFRMZxwsJm8';

export const PartThreeSlides = () => {
  const progress = useProgress();
  const lang = (['en', 'fi', 'sv'].includes(progress.language) ? progress.language : 'fi') as
    | 'en'
    | 'fi'
    | 'sv';
  const history = useHistory();
  const partThreeBadge = localStorage.getItem('partThreeDone');
  const latestCompletedStep = Number(localStorage.getItem('latestCompletedStep'));
  const currentPoints = Number(localStorage.getItem('points'));

  return (
    <div className="flex flex-col min-h-screen justify-evenly p-16">
      <Box className="my-4">
        <h2 className="text-xl font-bold mb-2">{t.slide1_title[lang]}</h2>
        <p>{t.slide1_content[lang]}</p>
      </Box>
      <Box className="my-4">
        <h2 className="text-xl font-bold mb-2">{t.slide2_title[lang]}</h2>
        <p>{t.slide2_content[lang]}</p>
      </Box>
      <Box className="my-4">
        <h2 className="text-xl font-bold mb-2">{t.slide3_title[lang]}</h2>
        <p>{t.slide3_content[lang]}</p>
      </Box>
      <Box className="my-4" itemsCenter={false}>
        <h2 className="text-xl font-bold mb-2">{t.video_title[lang]}</h2>
        <div className="aspect-w-16 aspect-h-9">
          <iframe
            src={`https://www.youtube.com/embed/${YOUTUBE_ID}`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full rounded-lg  "
            style={{ minHeight: '500px' }}
          ></iframe>
        </div>
      </Box>
      <Button
        gradient={false}
        className="bg-gradient-to-r from-green-600 to-green-500 \
        hover:from-green-700 hover:to-green-600 w-1/4 flex-col self-end"
        onClick={() => {
          if (currentPoints != 40 && !partThreeBadge) {
            localStorage.setItem('points', String(currentPoints + 10));
            localStorage.setItem('partThreeDone', 'true');
          }
          if (latestCompletedStep < 3) {
            localStorage.setItem('latestCompletedStep', '3');
          }

          history.push('/crash-course');
        }}
      >
        {t.back[lang]}
      </Button>
    </div>
  );
};
